import { useState, useEffect } from "react";
import {
  Grid,
  Card,
  CircularProgress,
  Switch,
  useMediaQuery,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  IconButton,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material/styles";
import axios from "axios";
import { useSnackbar } from "notistack";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import ResizableDataGrid from "components/ResizableDataGrid";

function Banners() {
  const auth = localStorage.getItem("authToken");
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const isMedium = useMediaQuery(theme.breakpoints.down("md"));

  const [loading, setLoading] = useState(false);
  const [banners, setBanners] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [bannerLink, setBannerLink] = useState("");
  const [uploading, setUploading] = useState(false);
  const [bannerImageModal, setBannerImageModal] = useState({
    open: false,
    path: null,
  });
  const [editBannerModal, setEditBannerModal] = useState({
    open: false,
    banner_id: null,
    banner_url: "",
    path: "",
  });
  const [deleteModal, setDeleteModal] = useState({
    open: false,
    banner_id: null,
    path: "",
  });
  const [editSelectedFile, setEditSelectedFile] = useState(null);

  const columns = [
    {
      field: "banner_id",
      headerName: "ID",
      width: isSmall ? 70 : isMedium ? 90 : 100,
      minWidth: isSmall ? 60 : 80,
    },
    {
      field: "path",
      headerName: "Image",
      width: isSmall ? 180 : isMedium ? 230 : 280,
      minWidth: isSmall ? 140 : 190,
      renderCell: (params) => (
        <MDBox
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
          }}
        >
          <MDBox
            component="img"
            src={`https://pancham-bucket.s3.amazonaws.com/${params.value}`}
            onClick={(e) => {
              e.stopPropagation();
              setBannerImageModal({ open: true, path: params.value });
            }}
            sx={{
              width: isSmall ? 112 : isMedium ? 132 : 156,
              height: isSmall ? 62 : isMedium ? 74 : 88,
              objectFit: "cover",
              backgroundColor: "#f8f9fa",
              borderRadius: "md",
              boxShadow: theme.shadows[1],
              border: "1px solid",
              borderColor: "grey.300",
              cursor: "pointer",
            }}
          />
        </MDBox>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: isSmall ? 145 : isMedium ? 165 : 170,
      minWidth: isSmall ? 120 : 140,
      renderCell: (params) => (
        <MDBox display="flex" alignItems="center" gap={0.75}>
          <Switch
            checked={Boolean(params.value)}
            onChange={() =>
              handleStatusToggle(params.row.banner_id, !params.value)
            }
          />
          <Chip
            size="small"
            label={params.value ? "Active" : "Inactive"}
            color={params.value ? "success" : "default"}
            variant={params.value ? "filled" : "outlined"}
          />
        </MDBox>
      ),
    },
    {
      field: "banner_url",
      headerName: "Link",
      width: isSmall ? 150 : isMedium ? 180 : 200,
      minWidth: isSmall ? 120 : 150,
      renderCell: (params) => (
        <MDTypography
          component="a"
          href={params.value}
          target="_blank"
          rel="noreferrer"
          variant="caption"
          color="info"
          fontWeight="medium"
          sx={{
            cursor: "pointer",
            textDecoration: "underline",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {params.value || "No link"}
        </MDTypography>
      ),
    },
    {
      field: "updated_at",
      headerName: "Last Updated",
      width: isSmall ? 170 : isMedium ? 205 : 235,
      minWidth: isSmall ? 150 : 180,
      valueFormatter: (params) => new Date(params.value).toLocaleString(),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: isSmall ? 205 : isMedium ? 230 : 250,
      minWidth: isSmall ? 160 : 190,
      renderCell: (params) => (
        <MDBox
          display="flex"
          gap={0.75}
          flexDirection={isSmall ? "column" : "row"}
          alignItems={isSmall ? "stretch" : "center"}
        >
          <MDButton
            variant="gradient"
            color="info"
            size="small"
            onClick={() => handleUpdateImageClick(params.row)}
            sx={{
              width: isSmall ? "100%" : "auto",
              minWidth: isSmall ? 0 : 96,
              px: 1,
              py: 0.6,
              fontSize: "0.65rem",
            }}
          >
            Update
          </MDButton>
          <MDButton
            variant="gradient"
            color="error"
            size="small"
            onClick={() => handleDelete(params.row.banner_id, params.row.path)}
            sx={{
              width: isSmall ? "100%" : "auto",
              minWidth: isSmall ? 0 : 66,
              px: 1,
              py: 0.6,
              fontSize: "0.65rem",
            }}
          >
            Delete
          </MDButton>
        </MDBox>
      ),
    },
  ];

  useEffect(() => {
    fetchBanners();
  }, []);

  const fetchBanners = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_BASEURL}/admin/banner/getall`, {
        headers: { Authorization: `Bearer ${auth}` },
      });
      if (res.data.success) {
        const sortedBanners = [...(res.data.data || [])].sort(
          (a, b) => Number(a.banner_id) - Number(b.banner_id)
        );
        setBanners(sortedBanners);
      }
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Error fetching banners", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleStatusToggle = async (bannerId, newStatus) => {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BASEURL}/admin/banner/update-status`,
        { banner_id: bannerId, status: newStatus },
        { headers: { Authorization: `Bearer ${auth}` } }
      );
      if (res.data.success) {
        enqueueSnackbar("Status updated", { variant: "success" });
        fetchBanners();
      }
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Error updating status", { variant: "error" });
    }
  };

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  const handleAddBanner = async () => {
    if (!selectedFile) {
      enqueueSnackbar("Please select an image", { variant: "warning" });
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append("image", selectedFile);
    formData.append("banner_url", bannerLink);

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BASEURL}/admin/banner/add`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${auth}`,
          },
        }
      );
      if (res.data.success) {
        enqueueSnackbar("Banner added successfully", { variant: "success" });
        setSelectedFile(null);
        setBannerLink("");
        setShowAdd(false);
        fetchBanners();
      }
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Error adding banner", { variant: "error" });
    } finally {
      setUploading(false);
    }
  };

  const handleUpdateImageClick = (banner) => {
    setEditBannerModal({
      open: true,
      banner_id: banner.banner_id,
      banner_url: banner.banner_url || "",
      path: banner.path,
    });
    setEditSelectedFile(null);
  };

  const handleUpdateBanner = async () => {
    setUploading(true);
    const { banner_id, banner_url, path } = editBannerModal;
    
    // Find the original banner to see what changed
    const original = banners.find(b => b.banner_id === banner_id);
    const urlChanged = original && original.banner_url !== banner_url;
    const imageChanged = editSelectedFile !== null;

    try {
      // 1. Update URL if changed
      if (urlChanged) {
        const resUrl = await axios.post(
          `${process.env.REACT_APP_BASEURL}/admin/banner/update-url`,
          { banner_id, banner_url },
          { headers: { Authorization: `Bearer ${auth}` } }
        );
        if (!resUrl.data.success) {
          enqueueSnackbar("Error updating URL", { variant: "error" });
        }
      }

      // 2. Update Image if changed
      if (imageChanged) {
        const formData = new FormData();
        formData.append("banner_id", banner_id);
        formData.append("image", editSelectedFile);
        
        const resImg = await axios.post(
          `${process.env.REACT_APP_BASEURL}/admin/banner/update-image`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${auth}`,
            },
          }
        );
        if (!resImg.data.success) {
          enqueueSnackbar("Error updating image", { variant: "error" });
        }
      }

      if (urlChanged || imageChanged) {
        enqueueSnackbar("Banner updated successfully", { variant: "success" });
        setEditBannerModal({ open: false, banner_id: null, banner_url: "", path: "" });
        fetchBanners();
      } else {
        setEditBannerModal({ open: false, banner_id: null, banner_url: "", path: "" });
      }

    } catch (err) {
      console.error(err);
      enqueueSnackbar("Error updating banner", { variant: "error" });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = (id, path) => {
    setDeleteModal({
      open: true,
      banner_id: id,
      path: path,
    });
  };

  const handleConfirmDelete = async () => {
    const { banner_id, path } = deleteModal;
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BASEURL}/admin/banner/delete`,
        { banner_id, path },
        { headers: { Authorization: `Bearer ${auth}` } }
      );
      if (res.data.success) {
        enqueueSnackbar("Banner deleted successfully", { variant: "success" });
        setDeleteModal({ open: false, banner_id: null, path: "" });
        fetchBanners();
      }
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Error deleting banner", { variant: "error" });
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems={isSmall ? "flex-start" : "center"}
                flexDirection={isSmall ? "column" : "row"}
                gap={isSmall ? 2 : 0}
              >
                <MDTypography variant="h6" color="white">
                  Banners Management
                </MDTypography>
                <MDButton
                  variant="outlined"
                  color="white"
                  onClick={() => setShowAdd(!showAdd)}
                >
                  {showAdd ? "Cancel" : "Add New Banner"}
                </MDButton>
              </MDBox>

              <Dialog
                open={showAdd}
                onClose={(event, reason) => {
                  if (reason !== "backdropClick") {
                    setShowAdd(false);
                  }
                }}
                maxWidth="sm"
                fullWidth
              >
                <DialogTitle sx={{ pr: 6 }}>
                  Add New Banner
                  <IconButton
                    aria-label="close"
                    onClick={() => setShowAdd(false)}
                    sx={{
                      position: "absolute",
                      right: 8,
                      top: 8,
                      color: (themeArg) => themeArg.palette.grey[500],
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </DialogTitle>
                <DialogContent>
                  <MDBox display="flex" flexDirection="column" gap={3} pt={2}>
                    <MDInput
                      type="text"
                      label="Banner Redirect Link"
                      value={bannerLink}
                      onChange={(e) => setBannerLink(e.target.value)}
                      placeholder="Enter URL (optional)"
                      fullWidth
                    />
                    <MDBox>
                      <MDTypography variant="button" fontWeight="regular" color="text" display="block" mb={1}>
                        Upload Banner Image
                      </MDTypography>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        style={{
                          width: "100%",
                          padding: "10px",
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                          cursor: "pointer"
                        }}
                      />
                    </MDBox>
                  </MDBox>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                  <MDButton
                    variant="outlined"
                    color="error"
                    onClick={() => setShowAdd(false)}
                    disabled={uploading}
                  >
                    Cancel
                  </MDButton>
                  <MDButton
                    variant="gradient"
                    color="success"
                    onClick={handleAddBanner}
                    disabled={uploading}
                  >
                    {uploading ? "Uploading..." : "Save Banner"}
                  </MDButton>
                </DialogActions>
              </Dialog>

              <MDBox pt={3}>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" p={5}>
                    <CircularProgress color="inherit" />
                  </MDBox>
                ) : (
                  <MDBox sx={{ height: isSmall ? 640 : 540 }} p={2}>
                    <ResizableDataGrid
                      getRowId={(row) => row.banner_id}
                      rows={banners}
                      columns={columns}
                      rowHeight={isSmall ? 118 : isMedium ? 106 : 98}
                      initialState={{
                        pagination: {
                          paginationModel: {
                            pageSize: 10,
                          },
                        },
                      }}
                      sx={{
                        "& .MuiDataGrid-columnHeaders": {
                          backgroundColor: "#f8f9fa",
                        },
                        "& .MuiDataGrid-cell": {
                          display: "flex",
                          alignItems: "center",
                          whiteSpace: "nowrap",
                        },
                        "& .MuiDataGrid-columnHeaderTitle": {
                          fontWeight: 700,
                        },
                        "& .MuiDataGrid-row:hover": {
                          backgroundColor: "#fafcff",
                        },
                      }}
                      disableRowSelectionOnClick
                    />
                  </MDBox>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Dialog
        open={bannerImageModal.open}
        onClose={() => setBannerImageModal({ open: false, path: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pr: 6 }}>
          Banner Preview
          <IconButton
            aria-label="close"
            onClick={() => setBannerImageModal({ open: false, path: null })}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (themeArg) => themeArg.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          {bannerImageModal.path && (
            <MDBox
              component="img"
              src={`https://pancham-bucket.s3.amazonaws.com/${bannerImageModal.path}`}
              sx={{
                width: "100%",
                height: "auto",
                maxHeight: "70vh",
                objectFit: "contain",
                borderRadius: "lg",
                display: "block",
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={editBannerModal.open}
        onClose={(event, reason) => {
          if (reason !== "backdropClick") {
            setEditBannerModal({ ...editBannerModal, open: false });
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pr: 6 }}>
          Update Banner
          <IconButton
            aria-label="close"
            onClick={() => setEditBannerModal({ ...editBannerModal, open: false })}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (themeArg) => themeArg.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <MDBox display="flex" flexDirection="column" gap={3} pt={2}>
            <MDInput
              type="text"
              label="Banner Redirect Link"
              value={editBannerModal.banner_url}
              onChange={(e) => setEditBannerModal({ ...editBannerModal, banner_url: e.target.value })}
              placeholder="Enter URL (optional)"
              fullWidth
            />
            <MDBox>
              <MDTypography variant="button" fontWeight="regular" color="text" display="block" mb={1}>
                Update Banner Image (leave empty to keep current)
              </MDTypography>
              <MDBox mb={2} display="flex" justifyContent="center">
                <MDBox
                  component="img"
                  src={`https://pancham-bucket.s3.amazonaws.com/${editBannerModal.path}`}
                  sx={{
                    width: 156,
                    height: 88,
                    objectFit: "cover",
                    borderRadius: "md",
                    border: "1px solid",
                    borderColor: "grey.300",
                  }}
                />
              </MDBox>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setEditSelectedFile(e.target.files[0])}
                style={{
                  width: "100%",
                  padding: "10px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  cursor: "pointer"
                }}
              />
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <MDButton
            variant="outlined"
            color="error"
            onClick={() => setEditBannerModal({ ...editBannerModal, open: false, banner_url: "", path: "" })}
            disabled={uploading}
          >
            Cancel
          </MDButton>
          <MDButton
            variant="gradient"
            color="success"
            onClick={handleUpdateBanner}
            disabled={uploading}
          >
            {uploading ? "Updating..." : "Save Changes"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteModal.open}
        onClose={() => setDeleteModal({ ...deleteModal, open: false })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <MDTypography variant="body2" color="text">
            Are you sure you want to delete this banner image? This action cannot be undone.
          </MDTypography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <MDButton
            variant="outlined"
            color="info"
            onClick={() => setDeleteModal({ ...deleteModal, open: false })}
          >
            Cancel
          </MDButton>
          <MDButton
            variant="gradient"
            color="error"
            onClick={handleConfirmDelete}
          >
            Delete
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default Banners;
